import React from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumb } from '../Breadcrumb';
import { ANALYTE_KINDS } from '../../utils/assayDefinition';
import { COLUMN_HELP, EXAMPLE_UPLOAD_COLUMNS, KIND_HELP, exampleAssay } from '../../utils/assayHelp';
import './AssayDefinition.css';

export const AssayGuidePage: React.FC = () => {
  const { meta, rows } = exampleAssay();
  const percent = rows.find(r => r.key === 'acid_insoluble_matter')!;
  const residue = rows.find(r => r.key === 'insolubles_mass')!;

  return (
    <div className="assay-page assay-guide">
      <Breadcrumb items={[{ label: 'Mariana Labs', path: '/dashboard' }, { label: 'Assays', path: '/assay' }, { label: 'How to define an assay' }]} />
      <h2 className="assay-page-title">How to define an assay</h2>

      <section>
        <h3>What an assay declares</h3>
        <p>
          An assay is a method the lab runs on a sample: a titration, a weighing, an instrument read. Its definition says
          <strong> what the method produces</strong>: one row per value a test of it reports, reads from the bench, or computes.
          Capital OS builds the entry sheet and the result pages from those rows, uploads are parsed against them, and
          nobody has to touch code for a new bench method.
        </p>
        <p>
          Anyone signed in can create an assay. It starts as a <strong>draft</strong>, hidden from the pickers in Capital OS, so you
          can take your time. When it is right, an admin promotes it to <strong>active</strong> and it is offered to the lab.
          <Link className="assay-guide-cta" to="/assay/new">Create a new assay</Link>
        </p>
      </section>

      <section>
        <h3>The example: {meta.title}</h3>
        <p>
          The SOP weighs a pretreated sample, dissolves it in HCl, filters the residue into a weighed crucible and dries it, then
          reports the residue as a percentage of the sample. Three numbers come off the bench, two are calculated, and the
          technician notes observations. As analytes:
        </p>
        <table className="analytes-table guide-table">
          <thead>
            <tr><th>Name</th><th>Key</th><th>Kind</th><th>Unit</th><th>Decimals</th><th>Headline</th><th>Formula</th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.key}>
                <td>{r.name}</td>
                <td><code>{r.key}</code></td>
                <td>{r.kind}</td>
                <td>{r.unit || <span className="assay-muted">none</span>}</td>
                <td>{r.decimals || <span className="assay-muted">—</span>}</td>
                <td className="center">{r.headline ? '✓' : ''}</td>
                <td>{r.formula ? <code>{r.formula}</code> : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p>
          While the New assay page's form is still empty, the <em>fill the form with a real example</em> link in its intro loads
          exactly this, so the quickest way to learn the form is to load it and change it into your assay.
        </p>
      </section>

      <section>
        <h3>The analyte fields</h3>
        <dl className="assay-guide-fields">
          <dt>Name</dt><dd>{COLUMN_HELP.name}</dd>
          <dt>Key</dt><dd>{COLUMN_HELP.key} The key fills itself from the name; only change it when you have a reason.</dd>
          <dt>Kind</dt><dd>{COLUMN_HELP.kind}</dd>
          <dt>Unit</dt><dd>{COLUMN_HELP.unit}</dd>
          <dt>Decimals</dt><dd>{COLUMN_HELP.decimals} The example reports the percentage to four places because the SOP asks for four below 0.01 %.</dd>
          <dt>Headline</dt><dd>{COLUMN_HELP.headline} In the example it is the percentage: the one number anyone asks for.</dd>
          <dt>Display</dt><dd>{COLUMN_HELP.display}</dd>
          <dt>Default / value</dt><dd>{COLUMN_HELP.defaultValue} The example offers 50 g for the sample mass because the SOP weighs 50 g.</dd>
        </dl>
      </section>

      <section>
        <h3>The kinds</h3>
        <dl className="assay-guide-fields">
          {ANALYTE_KINDS.map(kind => (
            <React.Fragment key={kind}>
              <dt><code>{kind}</code></dt>
              <dd>{KIND_HELP[kind].what} <span className="assay-muted">e.g. {KIND_HELP[kind].example}</span></dd>
            </React.Fragment>
          ))}
        </dl>
        <p>
          When in doubt: a number the operator types that is the answer is a <code>result</code>; a number they type that feeds a
          calculation is an <code>input</code>; a number the LIMS works out is <code>derived</code>; a number that never changes
          between runs is a <code>constant</code>; text is <code>meta</code>.
        </p>
      </section>

      <section>
        <h3>Formulas and worked examples</h3>
        <p>
          A <code>derived</code> row carries its formula: {COLUMN_HELP.formula} The LIMS evaluates it wherever a test is saved, so the
          number on the page is the number in the database, and a derived value can feed another one, as the example's
          percentage reads the residue mass. Derived rows are worked out top to bottom, which is why the residue row comes
          first; reorder rows with the ↑/↓ buttons:
        </p>
        <pre className="assay-guide-code">{residue.key} = {residue.formula}{'\n'}{percent.key} = {percent.formula}</pre>
        <p>
          Every formula needs a <strong>worked example</strong>: {COLUMN_HELP.workedExample} The SOP's own example does the job here:
          sample 50.00 g, empty crucible 32.4567 g, crucible plus residue 32.4617 g, residue 0.0050 g, result 0.0100 %. A formula
          that does not reproduce its worked example cannot be saved, which catches a swapped operand or a missing × 100 before a
          technician does.
        </p>
      </section>

      <section>
        <h3>Uploads</h3>
        <p>
          With capture set to <strong>upload</strong>, results arrive as a CSV or spreadsheet, one row per sample. On the Definition
          tab, drop one real file into the <em>Upload mapping</em> builder, say which row holds the headers and which column the
          sample id, and map each measured column onto a key. Calculated columns in the file are left unmapped: the LIMS computes
          them from the formulas. For the example's file:
        </p>
        <table className="mapping-table guide-table">
          <thead><tr><th>File column</th><th>Maps to</th></tr></thead>
          <tbody>
            {EXAMPLE_UPLOAD_COLUMNS.map(c => (
              <tr key={c.column}>
                <td>{c.column}</td>
                <td>{c.key === null ? <span className="assay-muted">not mapped (computed by the LIMS)</span> : c.key === 'sample id' ? <em>sample id column</em> : <code>{c.key}</code>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p>
          With capture set to <strong>manual</strong>, Capital OS builds an entry sheet from the rows instead: inputs and results are
          typed, derived values fill in as their inputs arrive.
        </p>
      </section>

      <section>
        <h3>From draft to active</h3>
        <p>
          Save the assay. It is a draft: hidden from Capital OS, editable by anyone signed in. Check the Definition tab's
          <em> Worksheet key audit</em> once tests exist, and ask an admin to promote it when it is right. After that, only an
          admin changes it, so the definition the lab is entering against does not shift under them. Retiring hides it again
          without losing its history.
        </p>
      </section>
    </div>
  );
};
